// Safe browser API wrapper
import type { Bookmarks, Tabs, TopSites } from 'webextension-polyfill';

// 检测是否在浏览器扩展模式下运行
export const isBrowserExtensionMode = () => {
  // 判断基本浏览器环境
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  
  // 首先检查环境变量
  if (import.meta.env.VITE_IS_WEB === 'true') return false;
  
  // 检查是否存在扩展API
  if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.id) {
    return true;
  }
  
  // 检查Firefox扩展环境 (使用 window 对象访问)
  if (typeof (window as any).browser !== 'undefined' && (window as any).browser.runtime && (window as any).browser.runtime.id) {
    return true;
  }
  
  // 检查扩展特有的URL协议
  if (window.location.protocol === 'chrome-extension:' || window.location.protocol === 'moz-extension:') {
    return true;
  }
  
  // 通过环境变量检测是否为扩展模式（作为备用方案）
  return import.meta.env.VITE_IS_WEB === 'false';
};

// 检测当前浏览器环境
export const isBrowserFirefox = () => {
  if (typeof window === 'undefined') return false;
  return navigator.userAgent.includes('Firefox');
};

// 安全地获取browser对象
const getBrowser = async () => {
  // 如果不是扩展模式，直接返回 null
  if (!isBrowserExtensionMode()) {
    console.warn('Not in extension mode, skipping browser API initialization');
    return null;
  }

  try {
    // 使用动态导入，确保只在浏览器环境中执行
    const browserModule = await import('webextension-polyfill');
    return browserModule.default || browserModule;
  } catch (error) {
    console.warn('Failed to load webextension-polyfill:', error);
    return null;
  }
};

// 书签相关API
export const getBookmarks = async () => {
  if (!isBrowserExtensionMode()) {
    return [];
  }
  
  try {
    const browser = await getBrowser();
    if (!browser || !browser.bookmarks) {
      console.warn('Browser bookmarks API not available');
      return [];
    }
    return await browser.bookmarks.getTree();
  } catch (error) {
    console.error('Error fetching bookmarks:', error);
    return [];
  }
};

export const openBookmarks = async () => {
  if (!isBrowserExtensionMode()) return;
  
  try {
    const browser = await getBrowser();
    if (!browser || !browser.tabs) {
      console.warn('Browser tabs API not available');
      return;
    }
    
    if (isBrowserFirefox()) {
      browser.tabs.create({ url: 'about:bookmarks' });
    } else {
      browser.tabs.create({ url: 'chrome://bookmarks' });
    }
  } catch (error) {
    console.error('Error opening bookmarks:', error);
  }
};

// 搜索相关API
export const performSearch = async (query: string) => {
  if (!isBrowserExtensionMode()) {
    // Web环境下使用标准搜索
    const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
    // 安全地访问window
    if (typeof window !== 'undefined') {
      window.location.href = searchUrl;
    }
    return;
  }

  try {
    const browser = await getBrowser();
    if (!browser) {
      throw new Error('Browser API not available');
    }
    
    if (browser.search) {
      // Chrome支持搜索API
      browser.search.query({
        text: query,
        disposition: 'CURRENT_TAB'
      });
    } else if (isBrowserFirefox()) {
      // Firefox没有search API，使用默认搜索引擎
      const url = 'https://www.google.com/search?q=' + encodeURIComponent(query);
      if (browser.tabs) {
        browser.tabs.update({ url });
      } else if (typeof window !== 'undefined') {
        window.location.href = url;
      }
    } else {
      // 其他情况回退到标准搜索
      const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
      if (typeof window !== 'undefined') {
        window.location.href = searchUrl;
      }
    }
  } catch (error) {
    console.error('Error performing search:', error);
    // 出错时回退到标准搜索
    if (typeof window !== 'undefined') {
      const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
      window.location.href = searchUrl;
    }
  }
};

// 处理扩展按钮点击行为
export const setupExtensionClickHandler = async () => {
  if (!isBrowserExtensionMode()) {
    console.warn('Skipping extension click handler setup - not in extension mode');
    return;
  }
  
  try {
    const browser = await getBrowser();
    if (!browser) {
      console.warn('Browser API not available - getBrowser returned null');
      return;
    }
    
    if (!browser.action?.onClicked) {
      console.warn('Browser action API not available - action or onClicked is undefined');
      return;
    }
    
    browser.action.onClicked.addListener((tab) => {
      if (browser.tabs) {
        browser.tabs.create({ url: 'src/pages/newtab/index.html' });
      }
    });
  } catch (error) {
    console.error('Error setting up extension click handler:', error);
  }
};

// Define Chrome's MostVisitedURL type
interface MostVisitedURL {
  url: string;
  title: string | undefined;
}

// 获取最常访问的站点
export const getTopSites = async (): Promise<{url: string, title: string}[]> => {
  if (!isBrowserExtensionMode()) {
    return [];
  }
  
  try {
    // 直接使用chrome API，因为这个功能在Firefox和Chrome中都可用
    if (typeof chrome !== 'undefined' && chrome.topSites) {
      return new Promise((resolve, reject) => {
        chrome.topSites.get((sites) => {
          if (chrome.runtime.lastError) {
            reject(new Error(chrome.runtime.lastError.message));
          } else {
            // Ensure consistent return type by mapping if needed
            const formattedSites = sites.map(site => ({
              url: site.url,
              title: site.title || ''  // Ensure title is always a string
            }));
            resolve(formattedSites);
          }
        });
      });
    }
    
    // 作为备选方案，尝试使用browser API
    const browser = await getBrowser();
    if (browser && browser.topSites) {
      const sites = await browser.topSites.get();
      // Ensure consistent return type
      return sites.map(site => ({
        url: site.url,
        title: site.title || ''  // Ensure title is always a string
      }));
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching top sites:', error);
    return [];
  }
};